import React from 'react';
import { LockOutlined } from '@ant-design/icons';

import { useXoeTools } from '../../hooks/xoe/useXoe';
import { EmptyState } from '../shared';
import XoeMark from './XoeMark';
import XoePromptCard from './XoePromptCard';
import { useXoeSize } from './XoeSurface';
import { resolvePromptCards } from '../../utils/xoe/suggestedPrompts';
import type { XoeHomeScreenProps } from '../../types/xoe/XoeUiTypes';

const COMPACT_MAX_CARDS = 4;

const XoeHomeScreen: React.FC<XoeHomeScreenProps> = ({ conversationUuid, patientLabel, onInsert, heading, unavailable }) => {
	const size = useXoeSize();
	// No request without a conversation; with one it shares the composer's ['xoeTools', uuid] cache entry.
	const { data: tools } = useXoeTools(unavailable ? '' : conversationUuid ?? '');

	if (unavailable) {
		// Fails closed and must never look like the empty state.
		return (
			<div className="mg-xoe-home mg-xoe-home--unavailable" role="status" data-testid="xoe-unavailable" data-reason={unavailable.reason}>
				<EmptyState
					icon={<LockOutlined className="mg-xoe-home-lock" />}
					title={unavailable.title}
					description={unavailable.body}
				/>
			</div>
		);
	}

	const { cards, source } = resolvePromptCards(tools, { hasPatient: !!patientLabel });
	const visible = size === 'compact' ? cards.slice(0, COMPACT_MAX_CARDS) : cards;

	return (
		<div className="mg-xoe-home" data-testid="xoe-home">
			<div className="mg-xoe-column mg-xoe-home-inner">
				<div className="mg-xoe-home-intro" data-testid="xoe-home-greeting">
					<span className="mg-xoe-home-mark">
						<XoeMark size={28} animated />
					</span>
					<div className="mg-xoe-home-greeting">
						{heading ?? (patientLabel ? `How can I help with ${patientLabel}?` : 'How can I help?')}
					</div>
					<div className="mg-xoe-home-sub">
						Ask about a patient, a result, a protocol, or the current literature. Xoe proposes; you confirm.
					</div>
				</div>

				<div className="mg-xoe-home-prompts" data-testid="xoe-home-prompts" data-source={source}>
					{visible.map((card) => (
						<XoePromptCard key={card.key} card={card} onSelect={onInsert} />
					))}
				</div>
			</div>
		</div>
	);
};

export default XoeHomeScreen;
